import { useEffect, useState } from 'react';
import { AlertCircle, ExternalLink, Gauge, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { ETALONNAGE_RESULTATS, type EtalonnageResultat } from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Panel des étalonnages / contrôles métrologiques d'un équipement (US-EQ-09).
 *
 *  - Liste triée par date desc.
 *  - Badge couleur sur le résultat (conforme/non_conforme/ajuste).
 *  - Alerte orange si prochain étalonnage < 30 j, rouge si échu.
 */

interface EtalonnageRow {
  id: string;
  date_etalonnage: string;
  organisme: string;
  resultat: EtalonnageResultat;
  prochain_etalonnage: string | null;
  document_url: string | null;
  observations: string | null;
}

export function EtalonnagesPanel({ equipementId }: { equipementId: string }) {
  const { can } = usePermissions();
  const canEdit = can('equipements', 'update');
  const canDelete = can('equipements', 'delete');

  const [items, setItems] = useState<EtalonnageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<EtalonnageRow | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const res = await api.get<{ data: EtalonnageRow[] }>(
        `/equipements/${equipementId}/etalonnages`,
      );
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipementId]);

  async function handleDelete(id: string) {
    if (!confirm('Supprimer cet étalonnage ?')) return;
    try {
      await api.delete(`/equipements/etalonnages/${id}`);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {canEdit && (
        <div className="flex justify-end">
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-1" />
            Nouvel étalonnage
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-gray-500 italic">Aucun étalonnage enregistré.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((e) => (
            <li
              key={e.id}
              className="rounded-lg border border-gray-200 p-3 bg-white flex items-start justify-between gap-2"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 flex items-center flex-wrap gap-2">
                  <Gauge className="w-3.5 h-3.5 text-gray-400" />
                  <span>{new Date(e.date_etalonnage).toLocaleDateString()}</span>
                  <ResultatBadge resultat={e.resultat} />
                  <span className="text-xs text-gray-500">{e.organisme}</span>
                </p>
                {e.prochain_etalonnage && <ProchainEtalonnage date={e.prochain_etalonnage} />}
                {e.observations && <p className="text-xs text-gray-600 mt-1">{e.observations}</p>}
                {e.document_url && (
                  <a
                    href={e.document_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-primary-600 hover:text-primary-700 inline-flex items-center mt-1"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Document
                  </a>
                )}
              </div>
              {(canEdit || canDelete) && (
                <div className="flex items-center space-x-1 flex-shrink-0">
                  {canEdit && (
                    <button
                      onClick={() => setEditing(e)}
                      className="p-1.5 rounded text-gray-600 hover:bg-gray-100"
                      title="Modifier"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(e.id)}
                      className="p-1.5 rounded text-red-600 hover:bg-red-50"
                      title="Supprimer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {creating && (
        <EtalonnageFormModal
          equipementId={equipementId}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
      {editing && (
        <EtalonnageFormModal
          equipementId={equipementId}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ProchainEtalonnage({ date }: { date: string }) {
  const target = new Date(date);
  const now = new Date();
  const diffDays = Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const isOverdue = diffDays < 0;
  const isClose = diffDays >= 0 && diffDays < 30;

  return (
    <p
      className={`text-xs mt-0.5 ${
        isOverdue
          ? 'text-red-700 font-medium'
          : isClose
            ? 'text-orange-700 font-medium'
            : 'text-gray-500'
      }`}
    >
      Prochain : {target.toLocaleDateString()}
      {isOverdue && ' (échu)'}
      {isClose && ` (dans ${diffDays} j)`}
    </p>
  );
}

function ResultatBadge({ resultat }: { resultat: EtalonnageResultat }) {
  const map: Record<EtalonnageResultat, string> = {
    conforme: 'bg-green-100 text-green-800 border-green-200',
    non_conforme: 'bg-red-100 text-red-800 border-red-200',
    ajuste: 'bg-orange-100 text-orange-800 border-orange-200',
  };
  return (
    <span
      className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium border ${map[resultat]}`}
    >
      {resultat.replace(/_/g, ' ')}
    </span>
  );
}

function EtalonnageFormModal({
  equipementId,
  existing,
  onClose,
  onSaved,
}: {
  equipementId: string;
  existing?: EtalonnageRow;
  onClose(): void;
  onSaved(): void;
}) {
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [date, setDate] = useState(
    existing
      ? new Date(existing.date_etalonnage).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10),
  );
  const [organisme, setOrganisme] = useState(existing?.organisme ?? '');
  const [resultat, setResultat] = useState<EtalonnageResultat>(existing?.resultat ?? 'conforme');
  const [prochain, setProchain] = useState(
    existing?.prochain_etalonnage
      ? new Date(existing.prochain_etalonnage).toISOString().slice(0, 10)
      : '',
  );
  const [docUrl, setDocUrl] = useState(existing?.document_url ?? '');
  const [observations, setObservations] = useState(existing?.observations ?? '');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!organisme.trim()) {
      setErrorMessage("L'organisme est requis.");
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      const payload = {
        date_etalonnage: date,
        organisme,
        resultat,
        prochain_etalonnage: prochain || null,
        document_url: docUrl || null,
        observations: observations || null,
      };
      if (existing) {
        await api.patch(`/equipements/etalonnages/${existing.id}`, payload);
      } else {
        await api.post(`/equipements/${equipementId}/etalonnages`, payload);
      }
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">
              {existing ? "Modifier l'étalonnage" : 'Nouvel étalonnage'}
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-3">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Date *">
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className={inputClass}
                  required
                />
              </Field>
              <Field label="Résultat *">
                <select
                  value={resultat}
                  onChange={(ev) => setResultat(ev.target.value as EtalonnageResultat)}
                  className={inputClass}
                >
                  {ETALONNAGE_RESULTATS.map((r) => (
                    <option key={r} value={r}>
                      {r.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Organisme *" className="col-span-2">
                <input
                  value={organisme}
                  onChange={(e) => setOrganisme(e.target.value)}
                  className={inputClass}
                  required
                />
              </Field>
              <Field label="Prochain étalonnage" className="col-span-2">
                <input
                  type="date"
                  value={prochain}
                  onChange={(e) => setProchain(e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="URL du document" className="col-span-2">
                <input
                  type="url"
                  value={docUrl}
                  onChange={(e) => setDocUrl(e.target.value)}
                  className={inputClass}
                  placeholder="https://..."
                />
              </Field>
              <Field label="Observations" className="col-span-2">
                <textarea
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  rows={3}
                  className={inputClass}
                />
              </Field>
            </div>
          </form>

          <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {existing ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}
